import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ThemeManager } from '@/lib/theme-manager';
import { useBranding } from '@/hooks/useBranding';

type AuthPage = 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'pending-approval';

interface PendingApprovalPageProps {
  onNavigate: (page: AuthPage) => void;
}

export function PendingApprovalPage({ onNavigate }: PendingApprovalPageProps) {
  const { branding } = useBranding();

  useEffect(() => {
    ThemeManager.getInstance().loadTheme();
  }, []);

  return (
    <div className="min-h-screen bg-charcoal-950 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Branding Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-royal-gradient flex items-center justify-center shadow-glow-lg">
            <img
              src={branding.logoUrl}
              alt={branding.appName}
              className="w-10 h-10 rounded-xl object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <h1 className="text-3xl font-bold text-royal-gradient mb-2 font-heading">
            {branding.appName}
          </h1>
          <p className="text-slate-400">{branding.tagline}</p>
        </div>

        {/* Info Card */}
        <div className="glass rounded-2xl p-8 shadow-glass text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          <h2 className="text-xl font-semibold text-white mb-3">Account Pending Approval</h2>
          <p className="text-slate-400 mb-6 leading-relaxed">
            Your account has been created successfully. An administrator needs to approve your
            account before you can sign in. You'll be able to log in once your account is activated.
          </p>

          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-6">
            <p className="text-sm text-amber-300">
              This usually takes less than 24 hours. If you need immediate access, contact your administrator.
            </p>
          </div>

          <button
            onClick={() => onNavigate('login')}
            className="px-6 py-2 rounded-lg bg-charcoal-800 border border-charcoal-700 text-white font-medium
                     hover:bg-charcoal-700 transition-all duration-200"
          >
            Back to Sign In
          </button>
        </div>
      </motion.div>
    </div>
  );
}
